import { useEffect, useRef } from 'preact/hooks';
import Plotly from 'plotly.js';
import { useTheme } from '../../contexts/ThemeContext';
import {
  getChartTitleColor,
  resolveSeriesColor,
} from '../../themes/chartThemeColors';
import { normalizeCurrencyCode } from '../../functions/calculations/currencyNormalizer';
import {
  plotlyConfig,
  plotlyFontSizes,
  plotlyTransparentBg,
  waterfallChartConfig,
} from './config';
import '../../css/waterfallChart.css';

export interface CategoryData {
  name: string;
  value: number;
  /** Theme color key or explicit CSS color (optional per category). */
  color?: string;
}

interface WaterfallChartProps {
  data: CategoryData[];
  title?: string;
  height?: number | string;
  width?: number | string;
  className?: string;
  formatValue?: (value: number) => string;
  currencyCode?: string;
}

const isTotalCategory = (name: string, index: number, total: number) =>
  name.toLowerCase() === 'total' || index === total - 1;

const wrapLabel = (str: string, maxLen: number) => {
  if (!str || str.length <= maxLen) return str;
  const words = str.split(' ');
  let result = '';
  let currentLine = '';

  words.forEach((word) => {
    if ((currentLine + word).length > maxLen) {
      result += (result ? '<br>' : '') + currentLine.trim();
      currentLine = word + ' ';
    } else {
      currentLine += word + ' ';
    }
  });
  result += (result ? '<br>' : '') + currentLine.trim();
  return result;
};

export const WaterfallChart = ({
  data,
  title = waterfallChartConfig.defaultTitle,
  height = waterfallChartConfig.defaults.height,
  width = waterfallChartConfig.defaults.width,
  className = '',
  currencyCode
}: WaterfallChartProps) => {
  const { theme } = useTheme();
  const plotRef = useRef<HTMLDivElement>(null);
  const cfg = waterfallChartConfig;
  const sizes = plotlyFontSizes;

  useEffect(() => {
    if (!plotRef.current || !data.length) {
      return;
    }

    const categories = data.map((item) => wrapLabel(item.name, cfg.label.wrapMaxLength));
    const absoluteValues = data.map((item) => item.value);

    const measures = data.map((item, index) =>
      isTotalCategory(item.name, index, data.length) ? 'total' : 'relative'
    );

    const values: number[] = [];
    for (let i = 0; i < absoluteValues.length; i++) {
      values.push(measures[i] === 'total' ? 0 : absoluteValues[i]);
    }

    const textLabels = values.map((value, index) => {
      if (measures[index] === 'total' || value === 0) return '';
      const formatted = Math.abs(value).toLocaleString('en-US', cfg.plot.numberFormat);
      return (value > 0 ? '+' : '-') + formatted;
    });

    const normalizedCurrency = normalizeCurrencyCode(currencyCode);
    const gridColor = resolveSeriesColor(theme, cfg.colors.grid);
    const axisColor = resolveSeriesColor(theme, cfg.colors.axis);
    const textColor = resolveSeriesColor(theme, cfg.colors.text);

    const trace: Plotly.Data = {
      type: 'waterfall',
      orientation: cfg.orientation,
      x: categories,
      y: values,
      measure: measures as Array<'relative' | 'total'>,
      text: textLabels,
      textposition: cfg.textposition,
      connector: {
        line: {
          color: resolveSeriesColor(theme, cfg.colors.connector),
        },
      },
      increasing: {
        marker: {
          color: resolveSeriesColor(theme, cfg.colors.increasing),
        },
      },
      decreasing: {
        marker: {
          color: resolveSeriesColor(theme, cfg.colors.decreasing),
        },
      },
      totals: {
        marker: {
          color: resolveSeriesColor(theme, cfg.colors.totals),
        },
      },
      hovertemplate: cfg.plot.hovertemplate,
    };

    let cumulative = 0;
    const cumulativeValues: number[] = [];

    for (let i = 0; i < values.length; i++) {
      if (measures[i] === 'total') {
        cumulative = absoluteValues[i];
      } else {
        cumulative += values[i];
      }
      cumulativeValues.push(cumulative);
    }

    const maxCumulative = Math.max(...cumulativeValues, 0);
    const minCumulative = Math.min(...cumulativeValues, 0);
    const range = maxCumulative - minCumulative;
    const padding =
      range > 0
        ? range * cfg.layout.yAxisPaddingRatio
        : Math.max(Math.abs(maxCumulative), Math.abs(minCumulative), 1) * cfg.layout.yAxisPaddingRatio;

    const margins = title ? cfg.layout.margins.withTitle : cfg.layout.margins.withoutTitle;

    const layout: Partial<Plotly.Layout> = {
      title: title
        ? {
            text: title.toUpperCase(),
            font: {
              family: theme.typography.fontFamily,
              size: sizes.titleFontSize,
              color: getChartTitleColor(theme),
              weight: 600,
            },
            x: 0,
            xanchor: 'left',
            y: 1,
            yanchor: 'top',
          }
        : undefined,
      font: {
        family: theme.typography.fontFamily,
        color: textColor,
        size: sizes.titleFontSize,
      },
      ...plotlyTransparentBg,
      margin: { ...margins },
      xaxis: {
        type: 'category',
        title: {
          text: '',
          font: {
            color: axisColor,
            size: sizes.titleFontSize,
          },
        },
        showgrid: cfg.layout.showXGrid,
        tickangle: cfg.layout.tickangle,
        automargin: true,
        tickfont: {
          color: textColor,
          size: cfg.label.tickFontSize,
        },
      },
      yaxis: {
        type: 'linear',
        title: {
          text: normalizedCurrency || '',
          font: {
            color: axisColor,
            size: sizes.titleFontSize,
          },
        },
        showgrid: cfg.layout.showYGrid,
        gridcolor: gridColor,
        zeroline: true,
        zerolinecolor: gridColor,
        zerolinewidth: cfg.layout.zerolineWidth,
        tickformat: cfg.layout.yTickformat,
        range: [minCumulative - padding, maxCumulative + padding],
      },
      autosize: true,
      showlegend: cfg.showLegend,
      hovermode: cfg.hovermode,
      width: typeof width === 'number' ? width : undefined,
      height: typeof height === 'number' ? height : undefined,
    };

    if (!Plotly || typeof Plotly.newPlot !== 'function') {
      return;
    }

    Plotly.newPlot(plotRef.current, [trace], layout, plotlyConfig).catch((error: unknown) => {
      console.error('Error plotting waterfall chart:', error);

      if (plotRef.current) {
        const errorDiv = document.createElement('div');
        const errorColor = resolveSeriesColor(theme, cfg.colors.errorText);
        errorDiv.style.cssText = `padding: 20px; text-align: center; color: ${errorColor};`;
        const errorMessage = error instanceof Error ? error.message : String(error);
        errorDiv.innerHTML = `
            <p><strong>Error rendering waterfall chart</strong></p>
            <p>${errorMessage || 'Unknown error'}</p>
            <p style="font-size: 12px; margin-top: 10px;">
              Note: Waterfall charts may require plotly.js (full) instead of plotly.js-basic-dist
            </p>
          `;
        plotRef.current.innerHTML = '';
        plotRef.current.appendChild(errorDiv);
      }
    });

    const handleResize = () => {
      if (plotRef.current) {
        Plotly.Plots.resize(plotRef.current);
      }
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (plotRef.current) {
        Plotly.purge(plotRef.current);
      }
    };
  }, [data, title, height, width, currencyCode, theme]);

  if (!data || data.length === 0) {
    return (
      <div className={`waterfall-chart-container ${className}`}>
        <div className="waterfall-chart-empty">
          <p>{cfg.emptyMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`waterfall-chart-container ${className}`}>
      <div ref={plotRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};
